import {
  cleanMessageBody,
  extractTimelineSnippet,
  isHtmlContent,
} from "@/lib/email/safe-email-body-utils";

/** Avoid pathological regex work on corrupted / huge blobs. */
const RAW_BODY_PROCESS_CAP = 450_000;

function decodeBasicHtmlEntities(text: string): string {
  let s = text.replace(/&nbsp;/gi, " ");
  s = s.replace(/&#x([\da-fA-F]{1,6});/gi, (_, hex: string) => {
    const code = Number.parseInt(hex, 16);
    if (!Number.isFinite(code) || code < 1) return "";
    try {
      return String.fromCodePoint(code);
    } catch {
      return "";
    }
  });
  s = s.replace(/&#(\d{1,6});/g, (_, dec: string) => {
    const code = Number.parseInt(dec, 10);
    if (!Number.isFinite(code) || code < 1) return "";
    try {
      return String.fromCodePoint(code);
    } catch {
      return "";
    }
  });
  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&apos;/gi, "'");
}

/**
 * Server-side HTML → readable plain text for model context.
 * Mirrors the timeline idea (strip scaffolding, newsletters) without DOMPurify;
 * sanitized HTML tags are removed so excerpts match user-visible meaning.
 */
export function htmlEmailToReadablePlain(html: string): string {
  const capped = html.length > RAW_BODY_PROCESS_CAP ? html.slice(0, RAW_BODY_PROCESS_CAP) : html;

  let s = capped.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/<style[\s\S]*?<\/style>/gi, "");

  s = s
    .replace(/<(?:br|hr)\s*\/?>/gi, "\n")
    .replace(/<\/(?:p|div|tr|h[1-6]|blockquote|section|article|header|footer|title|table|thead|tbody)>/gi, "\n")
    .replace(/<li\b[^>]*>/gi, "• ");

  s = s.replace(/<[^>]{0,320}>/g, " ");
  const decoded = decodeBasicHtmlEntities(s);
  return cleanMessageBody(decoded.replace(/[ \t]+\n/g, "\n").replace(/[ \t]{2,}/g, " "));
}

export interface CommunicationPreviewForModel {
  preview: string;
  /** True when heuristic detected HTML and we flattened before snippet extraction. */
  sourceWasLikelyHtml: boolean;
}

function toPlaintextForModel(body: unknown): CommunicationPreviewForModel & { plain: string } {
  if (!body || typeof body !== "string") {
    return { preview: "", plain: "", sourceWasLikelyHtml: false };
  }

  const raw = body.length > RAW_BODY_PROCESS_CAP ? body.slice(0, RAW_BODY_PROCESS_CAP) : body;
  const sourceWasLikelyHtml = isHtmlContent(raw);
  const plain = sourceWasLikelyHtml ? htmlEmailToReadablePlain(raw) : cleanMessageBody(raw);
  return { preview: plain, plain, sourceWasLikelyHtml };
}

/**
 * Produce a timeline-style excerpt for AI (reply chains trimmed like Hub timeline previews).
 */
export function buildCommunicationPreviewForModel(
  body: unknown,
  maxSnippetChars: number
): CommunicationPreviewForModel {
  const { plain, sourceWasLikelyHtml } = toPlaintextForModel(body);
  if (!plain) {
    return { preview: "", sourceWasLikelyHtml };
  }
  return {
    preview: extractTimelineSnippet(plain, maxSnippetChars),
    sourceWasLikelyHtml,
  };
}

/**
 * Full plaintext for tool/MCP callers that opt out of excerpt truncation.
 * Still strips HTML and cleans scanner/signature noise — never returns raw HTML.
 */
export function buildCommunicationPlaintextForModel(
  body: unknown
): CommunicationPreviewForModel {
  const { plain, sourceWasLikelyHtml } = toPlaintextForModel(body);
  return { preview: plain, sourceWasLikelyHtml };
}
